import React from 'react';
import { SlideViewProps } from './types';
import { SLIDE_HEIGHT, SLIDE_WIDTH, SlideView } from './SlideView';

export type ScaleFit = 'width' | 'contain';

interface BoxProps {
    /** `width` (default): fill the parent's width, height follows at 16:9. `contain`: fit inside the parent's width and height. */
    fit?: ScaleFit;
    className?: string;
    style?: React.CSSProperties;
    /** Styles on the 1920×1080 frame itself, in design px (e.g. a radius or shadow that scales with the slide). */
    innerStyle?: React.CSSProperties;
    /** Called with the current scale (rendered px per design px). */
    onScale?: (scale: number) => void;
}

// #region useBoxSize
function useBoxSize(ref: React.RefObject<HTMLElement>): { w: number; h: number } {
    const [size, setSize] = React.useState({ w: 0, h: 0 });
    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const read = (): void => {
            const r = el.getBoundingClientRect();
            setSize((s) => (Math.abs(s.w - r.width) < 0.5 && Math.abs(s.h - r.height) < 0.5 ? s : { w: r.width, h: r.height }));
        };
        read();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', read);
            return () => window.removeEventListener('resize', read);
        }
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return size;
}

// #region ScaleBox
/** Scales any 1920×1080 content to its box with a CSS transform, so text lays out identically at every size. */
export const ScaleBox: React.FunctionComponent<React.PropsWithChildren<BoxProps>> = ({ fit = 'width', className, style, innerStyle, onScale, children }): React.ReactElement => {
    const ref = React.useRef<HTMLDivElement>(null);
    const { w, h } = useBoxSize(ref);
    const scale = fit === 'contain' ? Math.min(w / SLIDE_WIDTH, h / SLIDE_HEIGHT) : w / SLIDE_WIDTH;
    const s = Number.isFinite(scale) && scale > 0 ? scale : 0;
    React.useEffect(() => { if (s) onScale?.(s); }, [s]);
    const outer: React.CSSProperties = fit === 'contain'
        ? { position: 'relative', width: '100%', height: '100%', ...style }
        : { position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', ...style };
    const left = fit === 'contain' ? (w - SLIDE_WIDTH * s) / 2 : 0;
    const top = fit === 'contain' ? (h - SLIDE_HEIGHT * s) / 2 : 0;
    return (
        <div ref={ref} className={className} style={outer}>
            <div style={{ position: 'absolute', left, top, width: SLIDE_WIDTH, height: SLIDE_HEIGHT, transform: `scale(${s})`, transformOrigin: '0 0', visibility: s ? 'visible' : 'hidden', ...innerStyle }}>
                {children}
            </div>
        </div>
    );
};

// #region ScaledSlide
/** A SlideView fitted to its parent (16:9 kept). Same props as SlideView, plus the box options. */
export const ScaledSlide: React.FunctionComponent<SlideViewProps & BoxProps> = ({ fit, className, style, innerStyle, onScale, ...slideProps }): React.ReactElement => (
    <ScaleBox fit={fit} className={className} style={style} innerStyle={innerStyle} onScale={onScale}>
        <SlideView {...slideProps} />
    </ScaleBox>
);
